import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

interface UserData {
  id: number;
  username: string;
  is_active: boolean;
  role: string;
  created_at: string;
}

const UserManagement: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Add user dialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState('user');
  const [addError, setAddError] = useState('');
  const [addLoading, setAddLoading] = useState(false);

  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UserData | null>(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/auth/users');
      setUsers(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('users.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async () => {
    setAddError('');

    if (!newUsername || !newPassword) {
      setAddError(t('auth.usernamePasswordRequired'));
      return;
    }

    if (newPassword.length < 6) {
      setAddError(t('auth.passwordTooShort'));
      return;
    }

    setAddLoading(true);
    try {
      await api.post('/api/auth/users', {
        username: newUsername,
        password: newPassword,
        role: newRole,
      });
      setSuccess(t('users.userCreated'));
      setAddDialogOpen(false);
      setNewUsername('');
      setNewPassword('');
      setNewRole('user');
      loadUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setAddError(err.response?.data?.detail || t('users.failedToCreate'));
    } finally {
      setAddLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      await api.delete(`/api/auth/users/${userToDelete.id}`);
      setSuccess(t('users.userDeleted'));
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      loadUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('users.failedToDelete'));
      setTimeout(() => setError(''), 3000);
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'superadmin':
        return { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' };
      case 'admin':
        return { background: '#1976d2', color: 'white' };
      default:
        return { background: '#e0e0e0', color: '#333' };
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {t('menu.userManagement')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {t('users.addUser')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/')}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {t('common.back')}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, boxShadow: '0 4px 12px rgba(102,126,234,0.15)' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ '& th': { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', fontWeight: 600 } }}>
              <TableCell>{t('auth.username')}</TableCell>
              <TableCell>{t('users.role')}</TableCell>
              <TableCell>{t('common.status')}</TableCell>
              <TableCell>{t('common.created')}</TableCell>
              <TableCell align="right">{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">{t('common.loading')}</TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">{t('users.noUsersFound')}</TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell sx={{ fontWeight: 500 }}>{user.username}</TableCell>
                  <TableCell>
                    <Chip
                      label={user.role}
                      size="small"
                      sx={getRoleColor(user.role)}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.is_active ? t('common.active') : t('common.inactive')}
                      size="small"
                      color={user.is_active ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      color="error"
                      onClick={() => {
                        setUserToDelete(user);
                        setDeleteDialogOpen(true);
                      }}
                      disabled={user.id === currentUser?.id}
                      title={user.id === currentUser?.id ? t('users.cannotDeleteSelf') : t('users.deleteUser')}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add User Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>{t('users.addNewUser')}</DialogTitle>
        <DialogContent>
          {addError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {addError}
            </Alert>
          )}
          <TextField
            fullWidth
            label={t('auth.username')}
            value={newUsername}
            onChange={(e) => setNewUsername(e.target.value)}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label={t('auth.password')}
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            margin="normal"
            required
            helperText={t('auth.minPasswordLength')}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>{t('users.role')}</InputLabel>
            <Select
              value={newRole}
              label={t('users.role')}
              onChange={(e) => setNewRole(e.target.value)}
            >
              <MenuItem value="user">{t('users.user')}</MenuItem>
              <MenuItem value="admin">{t('users.admin')}</MenuItem>
              <MenuItem value="superadmin">{t('users.superadmin')}</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleAddUser}
            disabled={addLoading}
            sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
          >
            {addLoading ? t('common.creating') : t('users.createUser')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('common.confirmDelete')}</DialogTitle>
        <DialogContent>
          {t('users.confirmDeleteUser')} <strong>{userToDelete?.username}</strong>?
          {' '}{t('common.cannotBeUndone')}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button color="error" variant="contained" onClick={handleDeleteUser}>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UserManagement;
